import { useMemo, useState } from 'react'
import {
  GetManagerRatingsReportQuery,
  ManagerRatingsReportFilterInput,
  useGetManagerRatingsReportQuery,
} from '@/graphql/generated'
import { LoadingSpinner } from '../atoms'
import { useErrorHandlerContext, useUserLanguage } from '@/customHooks'
import type { UserLanguage } from '@/custom-types'
import textsData from '@/textData/ManagerRatingsListTableTexts.json'

type ManagerRatingsForList =
  NonNullable<GetManagerRatingsReportQuery['managerRatingsReport']>['items'][number]

type ManagerRatingsListTableProps = {
  filters: ManagerRatingsReportFilterInput
}

const readableDate = (date: Date | string | null | undefined, locale: UserLanguage) => {
  if (!date) return '-'
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

const toRows = (items: ManagerRatingsForList[] | undefined) => {
  if (!items) return []
  return items.map((item) => ({
    ...item,
    tenantName: item.tenantName || '-',
    propertyName: item.propertyName || '-',
    unityName: item.unityName || '-',
    comment: item.comment || '',
  }))
}

export const ManagerRatingsListTable = ({ filters }: ManagerRatingsListTableProps) => {
  const userLanguage = useUserLanguage()
  const t = userLanguage === 'es' ? textsData.es : textsData.en
  const { open: errorHandle } = useErrorHandlerContext()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, loading, error } = useGetManagerRatingsReportQuery({
    variables: { filterInput: filters },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  const rows = useMemo(() => toRows(data?.managerRatingsReport?.items), [data])

  if (error) errorHandle(error)

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner text />
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="w-full py-4 text-center">
        <span className="font-bold">{t.noData}</span>
      </div>
    )
  }

  return (
    <div className="pt-4 pb-4 rounded-xl grid grid-cols-[1.2fr_1.8fr_1.6fr_1fr_60px] overflow-x-auto min-w-[950px]">
      <div className="contents">
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {t.columns.date}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {t.columns.tenant}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {t.columns.incident}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {t.columns.rating}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white" />
      </div>

      {rows.map((row, index) => {
        const rowBg = index % 2 !== 0 ? 'bg-background2' : 'bg-white'
        const isExpanded = expandedId === row.id
        return (
          <div key={row.id} className="contents">
            <div
              onClick={() => setExpandedId(isExpanded ? null : row.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {readableDate(row.createdAt, userLanguage)}
            </div>
            <div
              onClick={() => setExpandedId(isExpanded ? null : row.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {row.tenantName}
            </div>
            <div
              onClick={() => setExpandedId(isExpanded ? null : row.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {row.incidentId}
            </div>
            <div
              onClick={() => setExpandedId(isExpanded ? null : row.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {'★'.repeat(row.ratingValue)}
            </div>
            <div
              onClick={() => setExpandedId(isExpanded ? null : row.id)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center justify-center cursor-pointer ${rowBg}`}
            >
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {isExpanded && (
              <div className="col-start-1 col-end-6 bg-gray-50 pb-4 border-b border-gray-200">
                <div className="bg-white mx-4 mt-2 rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
                  <h4 className="font-bold text-primary2 border-b border-gray-100 pb-2">
                    {t.detail.title}
                  </h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {row.comment || t.detail.emptyComment}
                  </p>
                  <div className="grid md:grid-cols-3 gap-3 text-sm">
                    <span>
                      <strong>{t.detail.property}:</strong> {row.propertyName}
                    </span>
                    <span>
                      <strong>{t.detail.unity}:</strong> {row.unityName}
                    </span>
                    <span>
                      <strong>{t.detail.incident}:</strong> {row.incidentId}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
